import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';

@Injectable({ providedIn: 'root' })
export class SimpleCookieService {
  private decryptCookieValue(value: string, key?: string): string {
    if (!key || !value || !value.startsWith('U2FsdGVkX1')) {
      return value;
    }

    try {
      const decryptedValue = CryptoJS.AES.decrypt(value, key).toString(CryptoJS.enc.Utf8);
      return decryptedValue || value;
    } catch (error) {
      return value;
    }
  }

  private encryptCookieValue(value: string, key?: string): string {
    if (!key || !value) {
      return value;
    }

    try {
      return CryptoJS.AES.encrypt(value, key).toString();
    } catch (error) {
      return value;
    }
  }

  private readCookie(name: string): string | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const match = document.cookie
      .split('; ')
      .find((entry) => entry.startsWith(`${name}=`));

    if (!match) {
      return null;
    }

    return decodeURIComponent(match.split('=').slice(1).join('='));
  }

  private writeCookie(name: string, value: string, secure = false, expirySeconds?: number): void {
    if (typeof document === 'undefined') {
      return;
    }

    const { cookie } = document;
    const expires = typeof expirySeconds === 'number' && expirySeconds > 0
      ? `; expires=${new Date(Date.now() + expirySeconds * 1000).toUTCString()}`
      : '';
    const secureValue = secure ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=/${secureValue}`;

    try {
      window.localStorage.setItem(name, value);
    } catch (error) {
      // Ignore storage issues in restricted browsers.
    }
  }

  set(name: string, value: string, secure = false, key?: string, expirySeconds?: number | string, ..._rest: any[]): void {
    const expiry = typeof expirySeconds === 'string' ? Number(expirySeconds) : expirySeconds;
    const persistedValue = this.encryptCookieValue(value, key);
    this.writeCookie(name, persistedValue, secure, Number.isFinite(expiry) ? expiry : undefined);
  }

  get(name: string, _secure = false, key?: string): string {
    const cookieValue = this.readCookie(name);
    if (cookieValue !== null) {
      return this.decryptCookieValue(cookieValue, key);
    }

    try {
      const storedValue = window.localStorage.getItem(name);
      if (!storedValue) {
        return '';
      }

      return this.decryptCookieValue(storedValue, key);
    } catch (error) {
      return '';
    }
  }

  delete(name: string): void {
    if (typeof document !== 'undefined') {
      document.cookie = `${name}=; Max-Age=0; path=/`;
    }

    try {
      window.localStorage.removeItem(name);
    } catch (error) {
      // Ignore storage issues in restricted browsers.
    }
  }

  deleteAll(): void {
    if (typeof document !== 'undefined') {
      document.cookie.split(';').forEach((cookie) => {
        const name = cookie.trim().split('=')[0];
        if (name) {
          document.cookie = `${name}=; Max-Age=0; path=/`;
        }
      });
    }
  }
}
